import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import {
  forwardRef,
  useId,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';

export interface CheckboxProps extends Omit<
  ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
  'onCheckedChange'
> {
  onCheckedChange?: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
}

export const Checkbox = forwardRef<ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, label, description, onCheckedChange, id, checked, disabled, ...props }, ref) => {
    const generated = useId();
    const controlId = id ?? generated;

    const box = (
      <CheckboxPrimitive.Root
        ref={ref}
        id={controlId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(state) => onCheckedChange?.(state === true)}
        className={cn(
          'peer grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border border-input bg-muted/70 shadow-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
          'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary/20 data-[state=indeterminate]:text-primary',
          'aria-[invalid=true]:border-destructive',
          !label && className,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
          {checked === 'indeterminate' ? (
            <Minus className="h-3 w-3" strokeWidth={3} aria-hidden />
          ) : (
            <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );

    if (!label) return box;

    return (
      <div className={cn('flex items-start gap-2.5', className)}>
        <span className="flex h-5 items-center">{box}</span>
        <span className="grid gap-0.5">
          <label
            htmlFor={controlId}
            className={cn(
              'select-none text-sm leading-5 text-foreground',
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
            )}
          >
            {label}
          </label>
          {description && (
            <span className="text-xs leading-snug text-muted-foreground">{description}</span>
          )}
        </span>
      </div>
    );
  },
);
Checkbox.displayName = 'Checkbox';
